import type * as React from 'react'

import { clsx } from 'clsx'

import styles from './RootLayout.module.css'

import type { JSX } from 'react'

interface RootLayoutProps {
  children: React.ReactNode
  className?: string
  /** Set to null for a static loading frame with no interactive content. */
  skipLink?: React.ReactNode
}

/**
 * Root layout component that provides a CSS Grid-based layout system
 * for the entire publish application with fixed navbar, fixed sidebar,
 * and scrollable main content area.
 */
export function RootLayout({
  children,
  className,
  skipLink = (
    <a
      href="#main"
      className={styles.skipToMain}
      onClick={(e): void => {
        e.preventDefault()
        document.getElementById('main')?.focus()
      }}
    >
      Skip to main content
    </a>
  ),
}: RootLayoutProps): JSX.Element {
  return (
    <div className={clsx(styles.rootLayout, className)}>
      {skipLink}
      {children}
    </div>
  )
}

interface RootLayoutNavbarProps {
  children: React.ReactNode
  className?: string
}

/**
 * Fixed navbar area that stays at the top of the viewport.
 * Contains the AppShell navigation.
 *
 * Note: The "publish-navbar" id is observed by useDrawerHeight to
 * recalculate the mobile sidebar drawer height on banner changes.
 */
export function RootLayoutNavbar({
  children,
  className,
}: RootLayoutNavbarProps): JSX.Element {
  return (
    <header id="publish-navbar" className={clsx(styles.navbar, className)}>
      {children}
    </header>
  )
}

interface RootLayoutSidebarProps {
  children: React.ReactNode
  className?: string
}

/**
 * Fixed sidebar area that stays on the left side.
 * Does not scroll and will never grow over the viewport.
 * Uses Slot pattern to pass className to the child Sidebar component.
 */
export function RootLayoutSidebar({
  children,
  className,
}: RootLayoutSidebarProps): JSX.Element {
  return <div className={clsx(styles.sidebar, className)}>{children}</div>
}

interface RootLayoutMainProps {
  children: React.ReactNode
  className?: string
}

/**
 * Main content area that handles all scrolling behavior.
 * This is the only scrollable container that can grow over the viewport.
 */
export function RootLayoutMain({
  children,
  className,
}: RootLayoutMainProps): JSX.Element {
  return (
    <main id="main" tabIndex={-1} className={clsx(styles.main, className)}>
      {children}
    </main>
  )
}

RootLayout.Navbar = RootLayoutNavbar
RootLayout.Sidebar = RootLayoutSidebar
RootLayout.Main = RootLayoutMain
